import { Stack, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';

import { EdgeLabel } from '@/components/diagrams/components/EdgeLabel';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useClickOutside } from '@/hooks/useClickOutside';

import { useEstateWaterfallContext } from '../../contexts/estateWaterfall.context';
import { createEdgeId } from '../../waterfallGraph/waterfallGraph';
import { EdgeLabelPaginatedCards } from './edgeLabelPaginatedCards.component';
import { getComputedEdgeLabelProps } from './estateWaterfallEdgeLabel.utils';
import { EstateWaterfallEdgeLabelProps } from './types';
import { useEdgeLabelCards } from './useEdgeLabelCards.hook';

export function EstateWaterfallEdgeLabel(props: EstateWaterfallEdgeLabelProps) {
  const theme = useTheme();
  const { dispatch, state } = useEstateWaterfallContext();
  const [index, setIndex] = useState(0);
  const [openedByHover, setOpenedByHover] = useState(false);

  const {
    edge,
    source,
    target,
    label: externalLabel,
    value,
    variant,
    ...edgeLabelProps
  } = props;

  const edgeId = createEdgeId(source, target);

  const ref = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const { cards } = useEdgeLabelCards(props);

  const isOpen = state.edgeLabelClickedIds.has(edgeId);
  const isHighlighted = state.highlightedIds.has(edgeId);
  const hasAtLeastOneCard = cards.length > 0;
  const isInteractive = hasAtLeastOneCard && !state.presentationMode;
  const { computedLabel, computedValue, computedVariant } =
    getComputedEdgeLabelProps({
      cards,
      externalLabel,
      index,
      isOpen,
      value,
      variant,
    });

  // Card actions also close through the shared state.
  useEffect(() => {
    if (!isOpen) {
      setIndex(0);
      setOpenedByHover(false);
    }
  }, [isOpen]);

  useClickOutside(ref, () => {
    if (isOpen) {
      dispatch({ type: 'CLOSE_EDGE_LABEL', edgeId });
    }
    // We don't want to close the tooltip when clicking +/- zoom buttons or other controls
  }, ['[data-is-flow-chart-control-button]']);

  function onLabelClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (isOpen && openedByHover) {
      setOpenedByHover(false);
      return;
    }
    dispatch({ type: 'EDGE_LABEL_CLICKED', edgeId });
  }

  // Add a handler to stop propagation for all clicks inside the tooltip
  const handleTooltipContentClick = (event: React.MouseEvent) => {
    // Prevent clicks within the tooltip from bubbling up to the edge
    event.stopPropagation();
  };

  return (
    <Tooltip
      title={
        <Stack onClick={handleTooltipContentClick}>
          <EdgeLabelPaginatedCards
            cards={cards.map((c) => c.element)}
            index={index}
            setIndex={setIndex}
          />
        </Stack>
      }
      placement="bottom"
      open={isInteractive && isOpen}
      onOpen={() => {
        if (isInteractive && !isOpen) {
          setOpenedByHover(true);
          dispatch({ type: 'EDGE_LABEL_CLICKED', edgeId });
        }
      }}
      onClose={() => {
        if (openedByHover) {
          dispatch({ type: 'CLOSE_EDGE_LABEL', edgeId });
        }
      }}
      enterDelay={theme.transitions.duration.complex}
      enterNextDelay={theme.transitions.duration.complex}
      // Allow the pointer to cross the gap between the badge and its details.
      leaveDelay={theme.transitions.duration.shorter}
      disableHoverListener={!isInteractive}
      // Keyboard users open with the badge button. Pagination can disable the
      // focused arrow; that blur must not dismiss a hover-open card.
      disableFocusListener
      disableTouchListener
      slotProps={{
        popper: {
          disablePortal: false, // Needed to ensure the tooltip appears above the nodes
          ref,
          sx: { minWidth: 'max-content' },
          onClick: handleTooltipContentClick, // Also stop propagation at the popper level
        },
        // important! needed due to styled component wrapper of Tooltip not accepting overrides
        tooltip: { sx: { padding: '16px !important' } },
      }}
    >
      {/* React flow has weird element cloning logic to allow EdgeLabelRenderer to pop out of the normal DOM flow, so we have provide a specific popper ref to work around that */}
      <div ref={popperRef}>
        <EdgeLabel
          label={computedLabel}
          value={computedValue}
          highlight={isHighlighted}
          variant={computedVariant}
          onClick={isInteractive ? onLabelClick : undefined}
          {...edgeLabelProps}
        />
      </div>
    </Tooltip>
  );
}
